import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalLinkIcon, PlusIcon, VideoIcon, XIcon } from 'lucide-react';
import { Button, ConfirmDialog, DatePicker, EmptyState, ErrorNotice, Field, Panel, Pill, TextInput } from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { errorMessage } from '@/lib/api';
import { RECORDING_PROVIDER_LABELS, createEventRecording, deleteEventRecording, type EventRecording } from '@/lib/events';

/**
 * Meeting recordings on an event: the player when the host is one we embed
 * (Fathom, Loom, Google Drive), a link out otherwise, and who they are
 * shared with. Internal roles add and remove; contractors see what is
 * shared with them. Hosts gate their share pages with their own sign-in, so
 * the player prompts a viewer who is not signed in there.
 */
export function RecordingsPanel({
  eventId,
  recordings,
  canEdit,
}: {
  readonly eventId: string;
  readonly recordings: readonly EventRecording[];
  readonly canEdit: boolean;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<EventRecording | null>(null);
  const remove = useMutation({
    mutationFn: (recording: EventRecording) => deleteEventRecording(eventId, recording._id),
    onSuccess: async (_result, recording) => {
      toast.success(`Removed "${recording.title}"`);
      setRemoving(null);
      await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
    },
  });

  return (
    <Panel className="p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">Recordings</h2>
        {canEdit ? (
          <span data-guide="recordings-add">
            <Button variant="secondary" size="compact" onClick={() => setAdding(true)}>
              <PlusIcon aria-hidden className="size-4" /> Add recording
            </Button>
          </span>
        ) : null}
      </div>
      {recordings.length === 0 ? (
        <EmptyState
          icon={<VideoIcon aria-hidden className="size-6" />}
          title="No recordings yet"
          message="Paste the share link from wherever the call was recorded to keep it with the event. Fathom, Loom, and Google Drive links play here; anything else opens in a new tab."
          {...(canEdit ? { action: <Button onClick={() => setAdding(true)}>Add recording</Button> } : {})}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {recordings.map((recording) => (
            <div key={recording._id} data-guide="recording-card" className="rounded-portal-button border border-portal-line p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-portal-small font-bold">{recording.title}</p>
                  <p className="text-portal-micro text-portal-muted">
                    {recording.recordedAt ? new Date(recording.recordedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : 'Date not set'}
                    {' · '}
                    {recording.viewableBy.includes('contractor') ? 'Shared with facilitators' : 'BCE team only'}
                    {' · '}
                    {RECORDING_PROVIDER_LABELS[recording.provider] ?? RECORDING_PROVIDER_LABELS.link}
                  </p>
                </div>
                <span className="flex shrink-0 items-center gap-1">
                  <a href={recording.url} target="_blank" rel="noreferrer" aria-label={`Open ${recording.title}`} className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink">
                    <ExternalLinkIcon aria-hidden className="size-4" />
                  </a>
                  {canEdit ? (
                    <button type="button" data-guide="recording-remove" aria-label={`Remove ${recording.title}`} onClick={() => setRemoving(recording)} className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-rose-600">
                      <XIcon aria-hidden className="size-4" />
                    </button>
                  ) : null}
                </span>
              </div>
              {recording.embedUrl ? (
                <iframe
                  title={recording.title}
                  src={recording.embedUrl}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  className="mt-3 aspect-video w-full rounded-portal-button border border-portal-line bg-portal-bg"
                />
              ) : (
                <p className="mt-3 text-portal-micro text-portal-muted">
                  This host has no player here, so it opens in a new tab. <Pill tone="neutral">Link only</Pill>
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      {adding ? (
        <RecordingDialog
          eventId={eventId}
          onClose={() => setAdding(false)}
          onSaved={async () => {
            setAdding(false);
            await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
          }}
        />
      ) : null}
      <ConfirmDialog
        open={removing !== null}
        tone="danger"
        title={`Remove "${removing?.title ?? 'this recording'}"?`}
        message="Only the link comes off the event; the recording stays where it was recorded."
        confirmLabel="Remove"
        pending={remove.isPending}
        error={remove.isError ? errorMessage(remove.error) : undefined}
        onConfirm={() => {
          if (removing) remove.mutate(removing);
        }}
        onCancel={() => {
          if (!remove.isPending) setRemoving(null);
        }}
      />
    </Panel>
  );
}

function RecordingDialog({ eventId, onClose, onSaved }: { readonly eventId: string; readonly onClose: () => void; readonly onSaved: () => Promise<void> }): React.ReactElement {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [recordedAt, setRecordedAt] = useState('');
  const [shareWithFacilitators, setShareWithFacilitators] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      createEventRecording(eventId, {
        url: url.trim(),
        title: title.trim(),
        ...(recordedAt ? { recordedAt } : {}),
        viewableBy: shareWithFacilitators ? ['internal', 'contractor'] : ['internal'],
      }),
    onSuccess: async () => {
      toast.success(`Added "${title.trim()}"`);
      await onSaved();
    },
  });
  const valid = /^https:\/\//i.test(url.trim()) && title.trim().length > 0 && !save.isPending;
  return (
    <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-lg">
        <DialogTitle>Add recording</DialogTitle>
        <form
          className="mt-2 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) save.mutate();
          }}
        >
          <Field guide="recording-link" label="Recording link" htmlFor="recording-url" required hint="The share link from Fathom, Loom, Google Drive, or any other host. Fathom, Loom, and Drive links play on the event; others open in a new tab.">
            <TextInput id="recording-url" type="url" value={url} placeholder="https://" onChange={(event) => setUrl(event.target.value)} />
          </Field>
          <Field guide="recording-title" label="Title" htmlFor="recording-title" required hint="How the recording is listed on the event.">
            <TextInput id="recording-title" value={title} maxLength={200} placeholder="Client planning call" onChange={(event) => setTitle(event.target.value)} />
          </Field>
          <Field guide="recording-date" label="Recorded on" htmlFor="recording-date" hint="The day of the call.">
            <DatePicker id="recording-date" value={recordedAt} placeholder="Date" onChange={setRecordedAt} />
          </Field>
          <label data-guide="recording-share" className="flex items-center gap-2 text-portal-small">
            <input type="checkbox" checked={shareWithFacilitators} onChange={(event) => setShareWithFacilitators(event.target.checked)} />
            Share with the facilitators on this event
          </label>
          <p className="text-portal-micro text-portal-muted">Clients never see recordings.</p>
          {save.isError ? <ErrorNotice inline title="Not added" message={errorMessage(save.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button guide="recording-submit" type="submit" disabled={!valid}>
              {save.isPending ? 'Adding…' : 'Add recording'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
